import React, { FormEvent } from 'react';
import { Card, Button, Table, Flex, Progress, Text } from '@gravity-ui/uikit';
import { uploadVideo } from '../../libs/uploaders/uploadVideo';
import { formatFileSize } from '../../libs/formaters/formatFileSize';
import { UploadStatus } from '../../libs/uploaders/uploadVideo';
import { DetailedError } from 'tus-js-client';
import { toastError, toastSuccess, toastInfo } from '../../libs/toasts/toasts';
import { commonT, toastsT, videoUploadT } from '../../i18n';
import './styles/index.css';
import { DragAndDrop } from './components/DragAndDrop';
import { Profile } from '../../types/profiles/Profile';
import { MAX_VIDEO_TITLE_LENGTH } from '../../constants/MAX_VIDEO_TITLE_LENGTH';
import { ProfilesRecord } from '../Page/types/ProfilesRecord';
import { VideoInfoForm } from '../VideoInfoForm/VideoInfoForm';
import { vzhikLogger } from '../../libs/vzhik-logger/VzhikLogger';
import { MultipleUploadTable } from './components/MultipleUploadTable';
import { useMultipleVideoUpload } from '../../hooks/useMultipleVideoUpload';
import { getDefaultProfile } from '../../libs/utils/getProfileOptions';
import {
    getFilesFromClipboardData,
    getPasteShortcut,
    isEditablePasteTarget,
} from './utils/clipboardFiles';
import { filterValidVideoFiles } from './utils/videoFiles';

const INITIAL_STATE = {
    file: null,
    isVideoUploading: false,
    videoTitle: '',
    enableSummarization: false,
    videoUploadingProgress: 0,
    currentUploadPromise: Promise.resolve(null),
} as const;

type VideoUploadProps = {
    onUpload: () => void;
    onUploadStart?: (uploadUrl: string) => void;
    profiles: ProfilesRecord;
};
type VideoUploadState = {
    file: File | null;
    profile: Profile['name'];
    isVideoUploading: boolean;
    videoTitle: string;
    enableSummarization: boolean;
    videoUploadingProgress?: number;
    currentUploadPromise: Promise<{
        abort: () => Promise<void>;
        start: () => Promise<void>;
    } | null>;
    multipleFiles: File[];
};

function VideoUploadWithMultiple(props: VideoUploadProps) {
    const defaultProfile = getDefaultProfile(props.profiles);

    const multipleUpload = useMultipleVideoUpload({
        defaultProfile,
        onUploadSuccess: props.onUpload,
        onUploadStart: props.onUploadStart,
    });

    return (
        <VideoUploadInner
            {...props}
            multipleUpload={multipleUpload}
            defaultProfile={defaultProfile}
        />
    );
}

type VideoUploadInnerProps = VideoUploadProps & {
    multipleUpload: ReturnType<typeof useMultipleVideoUpload>;
    defaultProfile: string;
};

class VideoUploadInner extends React.PureComponent<VideoUploadInnerProps, VideoUploadState> {
    constructor(props: VideoUploadInnerProps) {
        super(props);

        this.state = {
            ...INITIAL_STATE,
            profile: props.defaultProfile || getDefaultProfile(props.profiles),
            multipleFiles: [],
        };
    }

    componentDidUpdate(prevProps: VideoUploadInnerProps) {
        if (
            (!this.state.profile && this.props.defaultProfile) ||
            (prevProps.defaultProfile !== this.props.defaultProfile && this.props.defaultProfile)
        ) {
            this.setState({ profile: this.props.defaultProfile });
        }
    }

    componentDidMount() {
        document.addEventListener('paste', this.handlePaste);
    }

    componentWillUnmount() {
        document.removeEventListener('paste', this.handlePaste);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    uploadFileInput = React.createRef<HTMLInputElement>();

    handleBeforeUnload = (event: BeforeUnloadEvent) => {
        // Recommended
        event.preventDefault();

        // Included for legacy support, e.g. Chrome/Edge < 119
        event.returnValue = true;
    };

    handleUploadBtnClick = () => {
        const input = this.uploadFileInput?.current;

        if (input?.value) {
            input.value = '';
        }

        input?.click();
    };

    handlePaste = (event: ClipboardEvent) => {
        if (!event.clipboardData) {
            return;
        }

        const files = getFilesFromClipboardData(event.clipboardData);

        if (files.length === 0) {
            if (!isEditablePasteTarget(event.target)) {
                toastError.open(toastsT('clipboardNoVideoFiles'));
            }

            return;
        }

        event.preventDefault();
        void this.handleClipboardFiles(files);
    };

    handleClipboardFiles = async (files: File[]) => {
        if (files.length === 0) {
            return;
        }

        const { validFiles } = await filterValidVideoFiles(files);

        if (validFiles.length === 0) {
            toastError.open(toastsT('clipboardNoVideoFiles'));
            return;
        }

        this.handleFilesSelected(validFiles);
        toastSuccess.open(toastsT('clipboardVideoFilesAdded', { count: validFiles.length }));
    };

    handleCancelBtn = () => {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);

        this.state.currentUploadPromise.then((currentUpload) => {
            if (currentUpload) {
                currentUpload.abort();
            }
        });

        this.setState({
            ...INITIAL_STATE,
            profile: this.props.defaultProfile || getDefaultProfile(this.props.profiles),
            multipleFiles: [],
        });
    };

    handleUploadStatusChange = (props: {
        status?: UploadStatus;
        progress?: number;
        error?: Error | DetailedError;
    }) => {
        switch (props.status) {
            case 'resume':
                toastInfo.open(toastsT('resumeUpload'));
                break;
            case 'error':
                this.setState({
                    isVideoUploading: false,
                });
                toastError.open(toastsT('uploadFailed'));
                vzhikLogger.logError({
                    message: 'Не удалось загрузить видео',
                    type: 'server',
                    additional: {
                        error: props.error,
                    },
                });
                window.removeEventListener('beforeunload', this.handleBeforeUnload);
                break;
            case 'success':
                this.setState({
                    ...INITIAL_STATE,
                    profile: this.props.defaultProfile || getDefaultProfile(this.props.profiles),
                    multipleFiles: [],
                });
                toastSuccess.open(toastsT('videoUploaded'));
                this.props.onUpload();
                window.removeEventListener('beforeunload', this.handleBeforeUnload);
                break;
            case 'progress':
                this.setState({ videoUploadingProgress: props.progress });
                break;
            default:
                break;
        }
    };

    handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!this.state.file || !this.state.profile) {
            return;
        }

        this.setState({ isVideoUploading: true });
        window.addEventListener('beforeunload', this.handleBeforeUnload);

        const currentUploadPromise = uploadVideo({
            file: this.state.file,
            videoTitle:
                this.state.videoTitle || this.state.file.name.slice(0, MAX_VIDEO_TITLE_LENGTH - 1),
            profile: this.state.profile,
            enableSummarization: this.state.enableSummarization,
            onUploadStateChange: this.handleUploadStatusChange,
            onUploadStart: this.props.onUploadStart,
        });

        this.setState({ currentUploadPromise });

        const uploadPromise = await currentUploadPromise;
        if (uploadPromise) {
            await uploadPromise.start();
        }
    };

    handleFilesSelected = (files: File[]) => {
        if (files.length === 0) {
            return;
        }

        if (files.length === 1 && this.props.multipleUpload.files.length === 0) {
            const file = files[0];
            this.setState({ file, videoTitle: file?.name ?? '', multipleFiles: [] });
        } else {
            this.props.multipleUpload.addFiles(files);
            this.setState({ file: null, multipleFiles: files });
        }
    };

    createTableData = (file: File) => {
        return [
            {
                name: file.name,
                size: formatFileSize(file.size),
                cancel: (
                    <Button view="outlined" type="reset">
                        {commonT('cancel')}
                    </Button>
                ),
            },
        ];
    };

    render() {
        const { multipleUpload } = this.props;

        const isMultipleMode = multipleUpload.files.length > 0;

        return (
            <form
                onSubmit={this.handleSubmit}
                onReset={this.handleCancelBtn}
                method={'post'}
                className="videoupload-form"
                autoComplete="off"
            >
                {isMultipleMode ? (
                    <Card className="card" type={'container'}>
                        <MultipleUploadTable
                            files={multipleUpload.files}
                            profiles={this.props.profiles}
                            isUploading={multipleUpload.isUploading}
                            onUpdateTitle={multipleUpload.updateTitle}
                            onUpdateProfile={multipleUpload.updateProfile}
                            onToggleSummarization={multipleUpload.toggleSummarization}
                            onRemove={multipleUpload.removeFile}
                            onStartSingle={multipleUpload.startUploadSingle}
                            onStartAll={multipleUpload.startUploadAll}
                            onStopUpload={multipleUpload.stopUpload}
                            onCancel={() => {
                                multipleUpload.clearAll();
                                this.setState({ multipleFiles: [] });
                            }}
                            onBulkUpdateProfile={multipleUpload.bulkUpdateProfile}
                            onBulkToggleSummarization={multipleUpload.bulkSetSummarization}
                        />
                    </Card>
                ) : this.state.file ? (
                    <Card className="card" type={'container'}>
                        <Flex direction="column" gap={5}>
                            <Table
                                columns={[
                                    {
                                        id: 'name',
                                        name: commonT('file'),
                                        width: '100%',
                                    },
                                    {
                                        id: 'size',
                                        name: '',
                                    },
                                    {
                                        id: 'cancel',
                                        name: '',
                                    },
                                ]}
                                verticalAlign="middle"
                                data={this.state.file ? this.createTableData(this.state.file) : []}
                            />
                            <VideoInfoForm
                                profile={this.state.profile}
                                profiles={this.props.profiles}
                                name={this.state.videoTitle}
                                enableSummarization={this.state.enableSummarization}
                                placeholder={this.state.file?.name}
                                padding="15px"
                                onChangeName={(videoTitle) => this.setState({ videoTitle })}
                                onChangeProfile={(profile) => this.setState({ profile })}
                                onToggleSummarization={() =>
                                    this.setState({
                                        enableSummarization: !this.state.enableSummarization,
                                    })
                                }
                                disabled={this.state.isVideoUploading}
                            />
                            <Button
                                view="action"
                                size="l"
                                type="submit"
                                loading={this.state.isVideoUploading}
                                disabled={
                                    !this.state.file ||
                                    !this.state.profile ||
                                    this.state.videoTitle.length > MAX_VIDEO_TITLE_LENGTH
                                }
                            >
                                {commonT('upload')}
                            </Button>

                            {this.state.isVideoUploading && (
                                <Progress
                                    theme={'success'}
                                    value={this.state.videoUploadingProgress ?? 0}
                                    text={videoUploadT('uploadProgress', {
                                        progress: this.state.videoUploadingProgress,
                                    })}
                                />
                            )}
                        </Flex>
                    </Card>
                ) : (
                    <Card className="card" type={'container'}>
                        <Button
                            className="choose-file-button"
                            view="normal"
                            size="l"
                            type="button"
                            onClick={this.handleUploadBtnClick}
                        >
                            {commonT('chooseFiles')}
                        </Button>
                        <br />
                        <Text color="secondary">
                            {videoUploadT('dragAndDropMultiple', {
                                shortcut: getPasteShortcut(),
                            })}
                        </Text>
                    </Card>
                )}
                <DragAndDrop
                    onSuccess={this.handleFilesSelected}
                    inputRef={this.uploadFileInput}
                    multiple={true}
                />
            </form>
        );
    }
}

export { VideoUploadWithMultiple as VideoUpload };
